import type { JSX, ReactNode, ComponentPropsWithoutRef } from 'react';
import { Children } from 'react';
import { styled } from 'styled-components';
import { useMq } from '../../hooks/useMq';

type StyledBreadcrumbsProps = {
  children: ReactNode;
};

const StyledBreadcrumbs = styled.nav<StyledBreadcrumbsProps>`
  display: flex;
  align-items: center;
  gap: var(--wui-space-01);
  overflow: hidden;

  > [data-wui-breadcrumb-divider]:last-of-type {
    display: none;
  }
`;

export type BreadcrumbsProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * Should be at least one `Breadcrumb` component
   */
  children: ReactNode;
};

const BUFFER_WIDTH = 10;

/**
 * Used to display a screens's location within a hierarchical structure. Each `Breadcrumb` is a composed version of
 * [Button](?path=/docs/components-button--docs). See it's documentation for additional functionality.
 */
export const Breadcrumbs = ({ children, ...props }: BreadcrumbsProps): JSX.Element => {
  const { isXsAndDown } = useMq();
  let crumbs = Children.toArray(children);
  if (isXsAndDown) {
    crumbs = crumbs.slice(-1);
  }

  const crumbMaxWidth = Math.floor(100 / crumbs.length) - BUFFER_WIDTH;

  return (
    <>
      {/* Hack since styled components v5 doesn't support container queries */}
      <style type="text/css">
        {`
       [data-wui-breadcrumbs] {
          container-type: inline-size;
          container-name: breadcrumbs;
        }

        @container breadcrumbs (width < 100vw) {
          [data-wui-breadcrumb] {
              max-width: ${crumbMaxWidth}cqw;
          }
        }
     `}
      </style>
      <StyledBreadcrumbs
        aria-label="Breadcrumbs"
        data-wui-breadcrumbs={true}
        {...props}
      >
        {crumbs}
      </StyledBreadcrumbs>
    </>
  );
};

Breadcrumbs.displayName = 'Breadcrumbs';
